import axios, { AxiosError } from 'axios'
import { WebApiError } from './webApiError'
import {
  CartAddInput,
  DeleteWatchdogArgs,
  GetWidgetsData,
  WatchdogArgs,
  WatchdogAvailabilityResponse,
  WatchdogReponseError,
  WatchdogResponse,
  WatchdogResult,
  SocialLoginResponse,
} from '~/utils/webApi.types'
import { GQLLocalMutationDeleteCartWarrantyArgs } from '~/graphql/__generated__/interfacesLocal'

export class WebApi {
  private csrfToken: string
  private axiosInstance = axios.create()

  constructor(csrfToken?: string, monolithUrl: string = '') {
    if (csrfToken) {
      this.csrfToken = csrfToken
    }
    this.axiosInstance = axios.create({
      headers: { csrfToken },
      baseURL: monolithUrl,
    })
  }

  async getWidgetData(): Promise<GetWidgetsData> {
    return await this.axiosInstance
      .get('/api/widgets/getWidgetsData')
      .then((res) => res.data)
  }

  async putToCart(body: CartAddInput) {
    const res = await this.axiosInstance.put('/api/cart/put', body)
    return res.data
  }

  async changeAmountInCart(body: {
    amount: number
    cartId: string
    id: string
  }) {
    const res = await this.axiosInstance.post('/api/cart/changeAmount', body)
    return res.data
  }

  async setPanelConsent(): Promise<void> {
    await this.axiosInstance.get('/api/privacy-settings/setPanelConsent')
  }

  async deleteFromCart(
    articleId: string,
    cartId: string
  ): Promise<GetWidgetsData> {
    const res = await this.axiosInstance.delete(
      `/api/cart/delete?id=${articleId}&cartId=${cartId}`
    )
    return res.data
  }

  async deleteDigitalContentFromCart({
    id,
    bundleId,
    cartId,
  }: {
    id: string
    bundleId: string
    cartId: string
  }): Promise<GetWidgetsData> {
    const res = await this.axiosInstance.delete(
      `/api/cart/delete-digital?id=${id}&bundleId=${bundleId}&cartId=${cartId}`
    )
    return res.data
  }

  async deleteInsuranceFromCart(
    params: GQLLocalMutationDeleteCartWarrantyArgs
  ): Promise<GetWidgetsData> {
    const response = await this.axiosInstance.delete(
      '/api/cart/delete-insurance',
      {
        params,
      }
    )
    return response.data
  }

  async deleteWarrantyFromCart(
    params: GQLLocalMutationDeleteCartWarrantyArgs
  ): Promise<GetWidgetsData> {
    const response = await this.axiosInstance.delete(
      '/api/cart/delete-warranty',
      {
        params,
      }
    )
    return response.data
  }

  async deleteReturnGuaranteeFromCart(
    params: GQLLocalMutationDeleteCartWarrantyArgs
  ): Promise<GetWidgetsData> {
    const response = await this.axiosInstance.delete(
      '/api/cart/delete-guarantee',
      {
        params,
      }
    )
    return response.data
  }

  async getVipConsent(): Promise<{ viewed: boolean }> {
    const response = await this.axiosInstance.get('/api/vip/get-modal-viewed')
    return response.data
  }

  async setVipConsent(): Promise<void> {
    await this.axiosInstance.get('/api/vip/save-modal-viewed')
  }

  async login({ username, password }: { username: string; password: string }) {
    const userLoginBody = { isCheckout: false, password, username }
    const res = await this.axiosInstance.post('/api/auth/login/', userLoginBody)
    return res.data
  }

  loginFb(body: { isFacebook: Boolean; token: string; userId: string }) {
    return this.axiosInstance.post('/api/auth/fbLogin/', body)
  }

  async loginGoogle(body: {
    credential: string
  }): Promise<SocialLoginResponse> {
    const res = await this.axiosInstance.post('/api/auth/googleLogin/', body)
    return res.data
  }

  async loginApple(body: {
    identityToken: string
    firstName?: string
    lastName?: string
    email?: string
  }): Promise<SocialLoginResponse> {
    const res = await this.axiosInstance.post('/api/auth/appleLogin/', body)
    return res.data
  }

  setConfirmAdultContent() {
    return this.axiosInstance.get('/?do=confirmAdultContent&confirm=1')
  }

  logout() {
    return this.axiosInstance.get(
      `/user/auth/logout?csrftoken=${this.csrfToken}`
    )
  }

  async addWatchdog({
    variantId,
    targetValue,
    type = 'PR',
    remove = 0,
  }: WatchdogArgs): Promise<WatchdogResult> {
    try {
      const url = `/api/watchdog/process?variantId=${variantId}&type=${type}&targetValue=${targetValue}&remove=${remove}`
      const res = await this.axiosInstance.get<WatchdogResponse>(url)
      return res.data
    } catch (e) {
      if ((e as any).isAxiosError) {
        const axiosError = e as AxiosError<WatchdogReponseError>
        return {
          message: axiosError.response?.data?.title ?? '',
          status: false,
        }
      }
      throw e
    }
  }

  async addWatchdogAvailability({
    variantId,
    route,
  }: {
    variantId: string
    route: string
  }): Promise<void> {
    const formData = new FormData()
    formData.append('variantId', variantId)
    const conjuctor = route.includes('?') ? '&' : '?'
    await this.axiosInstance.post(
      `${route}${conjuctor}do=watchdog&type=AV`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
      }
    )
  }

  async getWatchDogData({ variantId }: { variantId: string }) {
    const res = await this.axiosInstance.get<WatchdogAvailabilityResponse>(
      `/api/watchdog/get-data?variantId=${variantId}`
    )
    return res.data
  }

  async removeWatchdogAvailability({
    variantId,
    type,
  }: DeleteWatchdogArgs): Promise<void> {
    await this.axiosInstance.put(
      `/api/watchdog/process?variantId=${variantId}&type=${type}&remove=1`
    )
  }

  async trackXSell(data: XSellTrackData) {
    try {
      return await axios.post('/api/xsell/track', data)
    } catch (err) {
      throw new WebApiError({
        message: 'trackXSell: publish product view events',
        data: {
          body: JSON.stringify(data),
          responseBody: JSON.stringify((err as AxiosError)?.response?.data),
        },
      })
    }
  }

  /**
   *
   * @deprecated =  should be remove in GF-3372
   */
  async findVariants(entityIds: Array<string>) {
    const url = `/api/product/find-variants?ids=${entityIds}`
    const response = await this.axiosInstance.get(url)
    return response.data
  }
}

export enum XSELL_HIT_TYPES {
  CLICK = 'C',
  VIEW = 'V',
  PURCHASE = 'P',
}

/* eslint-disable camelcase */
export interface XSellTrackData {
  hit_type: XSELL_HIT_TYPES
  m_id: string
  placement: string
  s_id: string[]
  user: string | null
  transaction_id?: string
  bookmark_id?: string | null
  ga_client_id?: string | null
}
/* eslint-enable camelcase */

export const webApi = new WebApi()
